import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { googleAnalyticsService } from '../../services/googleAnalytics';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';

// Global lock to prevent duplicate OAuth processing
const OAUTH_LOCK_KEY = 'oauth_processing_lock';
const LOCK_TIMEOUT = 30000; // 30 seconds

// Cleanup function to remove stale locks
const cleanupStaleLocks = () => {
  const keys = Object.keys(sessionStorage);
  const now = Date.now();
  
  keys.forEach(key => {
    if (key.startsWith(OAUTH_LOCK_KEY)) {
      const timestamp = parseInt(sessionStorage.getItem(key) || '0');
      if (now - timestamp > LOCK_TIMEOUT) {
        sessionStorage.removeItem(key);
      }
    }
  });
};

export function GoogleCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error' | 'updating'>('processing');
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent duplicate execution using multiple mechanisms
      if (hasProcessed.current) return;
      
      // Clean up any stale locks first
      cleanupStaleLocks();
      
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      
      // Create a unique lock key for this specific OAuth code
      const lockKey = `${OAUTH_LOCK_KEY}_${code}`;
      
      // Check if this OAuth code is already being processed
      const existingLock = sessionStorage.getItem(lockKey);
      if (existingLock) {
        console.log('OAuth code already being processed, skipping...');
        return;
      }
      
      // Set the lock
      sessionStorage.setItem(lockKey, Date.now().toString());
      hasProcessed.current = true;
      
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setError('Google authentication was cancelled or failed');
        sessionStorage.removeItem(lockKey);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setError('Invalid callback parameters');
        sessionStorage.removeItem(lockKey);
        return;
      }

      try {
        const result = await googleAnalyticsService.handleOAuthCallback(code, state);
        
        if (result.success) {
          setStatus('success');
          
          // Check if we were connecting a specific project
          const projectId = localStorage.getItem('connecting_project_id');
          const isReconnecting = localStorage.getItem('reconnecting_ga4') === 'true';
          
                      if (projectId) {
              if (isReconnecting) {
                // For reconnections, update project tokens and redirect to connections tab
                console.log('Reconnection flow - updating project tokens');
                setStatus('updating');
                
                try {
                  // Get auth token
                  const { data: { session } } = await supabase.auth.getSession();
                  
                  if (!session?.access_token) {
                    throw new Error('Authentication required');
                  }

                  // Call the new update-tokens endpoint
                  const updateResponse = await fetch(`${import.meta.env.VITE_API_URL}/oauth/google/project/${projectId}/update-tokens`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${session.access_token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  
                  if (!updateResponse.ok) {
                    throw new Error(`Failed to update tokens: ${updateResponse.status}`);
                  }
                  
                  const updateResult = await updateResponse.json();
                  
                  if (updateResult.success) {
                    toast.success('Successfully reconnected GA4! Tokens updated.');
                    console.log('Project tokens updated successfully:', updateResult.data);
                  } else {
                    throw new Error(updateResult.error || 'Failed to update project tokens');
                  }
                  
                } catch (updateError) {
                  console.error('Error updating project tokens:', updateError);
                  toast.warning('Authentication successful, but please refresh the page to complete reconnection.');
                }
                
                // Clear flags
                localStorage.removeItem('connecting_project_id');
                localStorage.removeItem('reconnecting_ga4');
                
                // Redirect to project connections tab
                setTimeout(() => {
                  navigate(`/projects/${projectId}?tab=connections`);
                }, 2000);
                return;
              }
              
              // Try auto-connect for new connections only
              try {
                const autoConnectResult = await googleAnalyticsService.autoConnect(projectId);
                
                if (autoConnectResult.success && autoConnectResult.data) {
                  if (autoConnectResult.data.alreadyConnected) {
                    toast.info('GA4 property was already connected to your project');
                  } else {
                    toast.success(`Connected ${autoConnectResult.data.propertyName} to your project!`);
                  }
                  
                  // Clear the stored project ID
                  localStorage.removeItem('connecting_project_id');
                  
                  // Redirect back to the project page
                  setTimeout(() => {
                    navigate(`/projects/${projectId}`);
                  }, 2000);
                  return;
                }
              } catch (autoConnectError) {
                console.error('Auto-connect failed, falling back to manual selection:', autoConnectError);
              }
            
            // If auto-connect fails, try the manual approach
            try {
              const accounts = await googleAnalyticsService.getAccounts();
              
              // If there's only one account and one property, connect it manually
              if (accounts.length === 1 && accounts[0].properties.length === 1) {
                const account = accounts[0];
                const property = account.properties[0];
                
                // Connect the property to the project
                await googleAnalyticsService.connectProperty(
                  account.accountId,
                  property.propertyId,
                  property.propertyName,
                  projectId
                );
                
                toast.success(`Connected ${property.propertyName} to your project!`);
                // Show additional info about metadata processing
                setTimeout(() => {
                  toast.info('🔧 Setting up your GA4 metadata in the background - this may take a few moments');
                }, 1000);
                
                // Clear the stored project ID
                localStorage.removeItem('connecting_project_id');
                
                // Redirect back to the project page
                setTimeout(() => {
                  navigate(`/projects/${projectId}`);
                }, 2000);
                return;
              }
            } catch (connectError) {
              console.error('Error connecting property:', connectError);
            }
            
            // If we can't auto-connect, redirect to the project connections tab
            localStorage.removeItem('connecting_project_id');
            setTimeout(() => {
              navigate(`/projects/${projectId}?tab=connections`);
            }, 2000);
            return;
          }
          
          // If no specific project, check if we're in onboarding
          const isOnboarding = window.location.pathname.includes('onboarding') || 
                              sessionStorage.getItem('onboarding_in_progress') === 'true';
          
          // Check if we're connecting from project creation
          const isFromProjectCreation = sessionStorage.getItem('ga4_connecting_from_project_creation') === 'true';
          
          if (isFromProjectCreation) {
            // For project creation flow, redirect back to project creation with success flag
            console.log('OAuth completed from project creation, redirecting back');
            sessionStorage.removeItem('ga4_connecting_from_project_creation');
            sessionStorage.setItem('ga4_oauth_completed', 'true');
            toast.success('Successfully connected to Google Analytics!');
            setTimeout(() => {
              toast.info('Now choose which GA4 property you want to connect to your project');
            }, 1000);
            
            // Redirect back to projects page (the modal should reopen with GA4 connected)
            setTimeout(() => {
              navigate('/projects');
            }, 1500);
            return;
          }
          
          if (isOnboarding) {
            // For onboarding users, always redirect to property selection (no auto-connect)
            console.log('Setting oauth_completed flag and redirecting to step 4');
            sessionStorage.setItem('oauth_completed', 'true');
            // Clean up the onboarding flag since OAuth is now complete
            sessionStorage.removeItem('onboarding_in_progress');
            toast.success('Successfully connected to Google Analytics!');
            setTimeout(() => {
              toast.info('Now choose which GA4 property you want to connect to your project');
            }, 1000);
            
            // Always redirect to onboarding step 4 for manual property selection
            setTimeout(() => {
              console.log('Redirecting to /onboarding/4 for manual property selection');
              navigate('/onboarding/4');
            }, 1500);
            return;
          }
          
          // For non-onboarding users, redirect to dashboard
          // The App routing logic will handle redirecting incomplete users to onboarding
          navigate('/dashboard');
        } else {
          setStatus('error');
          setError(result.error || 'Authentication failed');
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setError(error instanceof Error ? error.message : 'An unexpected error occurred');
      } finally {
        // Clean up the lock after processing
        sessionStorage.removeItem(lockKey);
      }
    };

    handleCallback();
  }, [searchParams, navigate]); // Removed isProcessing from dependencies

  const handleRetry = () => {
    // Check if we were connecting a specific project
    const projectId = localStorage.getItem('connecting_project_id');
    if (projectId) {
      localStorage.removeItem('connecting_project_id');
      localStorage.removeItem('reconnecting_ga4'); // Clean up reconnection flag
      navigate(`/projects/${projectId}?tab=connections`);
    } else {
      // Check if we're in onboarding
      const isOnboarding = window.location.pathname.includes('onboarding');
      if (isOnboarding) {
        navigate('/onboarding/4');
      } else {
        navigate('/dashboard');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 text-center">
        {status === 'processing' && (
          <>
            <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Connecting to Google Analytics
            </h1>
            <p className="text-gray-600">
              Please wait while we complete the authentication process...
            </p>
          </>
        )}

        {status === 'updating' && (
          <>
            <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Updating Connection
            </h1>
            <p className="text-gray-600">
              Updating your project with fresh authentication tokens...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Successfully Connected!
            </h1>
            <p className="text-gray-600 mb-4">
              Your Google Analytics account has been connected. Redirecting you back...
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-green-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              Connection Failed
            </h1>
            <p className="text-gray-600 mb-6">
              {error || 'We encountered an error while connecting to Google Analytics.'}
            </p>
            <Button onClick={handleRetry} className="w-full">
              Return to Setup
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}